const isRecord = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

const stableValue = (value) => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!isRecord(value)) return value;
  return Object.keys(value).sort().reduce((output, key) => {
    if (value[key] !== undefined) output[key] = stableValue(value[key]);
    return output;
  }, {});
};

const valueFingerprint = (value) => JSON.stringify(stableValue(value));

const valuesMatch = (first, second) => valueFingerprint(first) === valueFingerprint(second);

const hasMeaningfulLegacyValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return Boolean(value.trim());
  if (typeof value === "boolean" || typeof value === "number") return true;
  if (Array.isArray(value)) return value.length > 0;
  if (isRecord(value)) return Object.values(value).some(hasMeaningfulLegacyValue);
  return true;
};

const mergeObjects = (target, patch) => {
  if (Array.isArray(patch)) return patch.map((value) => isRecord(value) ? mergeObjects({}, value) : value);
  if (!isRecord(patch)) return patch === undefined ? target : patch;
  const output = { ...(isRecord(target) ? target : {}) };
  Object.entries(patch).forEach(([key, value]) => {
    if (value === undefined) return;
    output[key] = isRecord(value) ? mergeObjects(output[key], value) : Array.isArray(value) ? mergeObjects([], value) : value;
  });
  return output;
};

export const profileFormFingerprint = (form = {}) => valueFingerprint(form);

export const changedProfileFormFields = (current = {}, next = {}) => {
  const keys = new Set([...Object.keys(current || {}), ...Object.keys(next || {})]);
  return [...keys].filter((key) => !valuesMatch(current?.[key], next?.[key]));
};

export const inferLegacyProfileDraftDirtyFields = (base = {}, draft = {}) => {
  if (!isRecord(draft)) return [];
  return Object.keys(draft).filter((key) => (
    !valuesMatch(base?.[key], draft[key])
    && hasMeaningfulLegacyValue(draft[key])
  ));
};

export const getProfileDraftDirtyFields = (base = {}, draftRecord = null) => {
  if (!isRecord(draftRecord?.form)) return [];
  if (Number(draftRecord.schema) >= 2 && Array.isArray(draftRecord.dirtyFields)) {
    return [...new Set(draftRecord.dirtyFields
      .map((field) => String(field || "").trim())
      .filter((field) => field && Object.hasOwn(draftRecord.form, field)))];
  }
  return inferLegacyProfileDraftDirtyFields(base, draftRecord.form);
};

export const getPendingProfileDraftDirtyFields = (base = {}, draftRecord = null) => (
  getProfileDraftDirtyFields(base, draftRecord).filter((field) => (
    !valuesMatch(base?.[field], draftRecord?.form?.[field])
  ))
);

export const mergeProfileDraftForm = (base = {}, draft = {}, dirtyFields = null) => {
  if (!isRecord(draft)) return base;
  const selectedFields = dirtyFields === null
    ? Object.keys(draft)
    : [...new Set(Array.from(dirtyFields || []).map((field) => String(field || "").trim()).filter(Boolean))];
  const selectedPatch = selectedFields.reduce((output, field) => {
    if (Object.hasOwn(draft, field)) output[field] = draft[field];
    return output;
  }, {});
  return mergeObjects(base, selectedPatch);
};
